import { ExpandLess, ExpandMore } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { Drawer, IconButton, List } from '@mui/material';
import clsx from 'clsx';
import { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useLocation, useNavigate } from 'react-router-dom';

import { Collapsed } from '@/components/Collapsed';
import { useYaMetrika } from '@/entities/yaMetrika';
import { HelpModal } from '@/features/HelpModal';
import { AnalyticsEvent } from '@/types/yametrika';

import { SidebarLink } from './SidebarLink';
import { bottomLinks, links, trackerLink, v2Group } from './config';

import s from './Sidebar.module.scss';

const drawerWidthCollapsed = 45;
const drawerWidthExpanded = 180;

const Sidebar = () => {
  const [open, setOpen] = useState(false);
  const [openHelp, setOpenHelp] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { sendEvent } = useYaMetrika();

  // Вложенные экраны раздела (/tracker/boards) тоже подсвечивают пункт
  const isLinkActive = (link: string) =>
    !!link && (location.pathname === link || location.pathname.startsWith(`${link}/`));

  const isV2Active = v2Group.links.some(({ link }) => isLinkActive(link));
  // На странице нового учёта группа раскрыта, чтобы было видно, где ты
  const [v2Opened, setV2Opened] = useState(isV2Active);

  const toggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  // Сохраняем ?user= и даты при переходе между экранами
  const goTo = (link: string) => {
    const search = window.location.search;
    navigate(search ? `${link}${search}` : link);
  };

  useHotkeys('shift+H', (e) => {
    e.preventDefault();
    setOpenHelp(true);
    sendEvent(AnalyticsEvent.useHotkeys);
  });

  const visibleBottomLinks = bottomLinks.filter((item) => item.link);

  return (
    <Drawer
      className={s.root}
      variant="permanent"
      sx={{
        width: open ? drawerWidthExpanded : drawerWidthCollapsed,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidthExpanded : drawerWidthCollapsed,
          transition: 'width 0.3s',
          overflowX: 'hidden',
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          backgroundColor: 'var(--accent-color)',
        },
      }}
    >
      <div className={s.root__burger}>
        <IconButton onClick={toggleDrawer}>
          <MenuIcon />
        </IconButton>
      </div>

      <List className={s.root__list}>
        {links.map(({ text, icon, link }) => (
          <SidebarLink
            key={text}
            text={text}
            icon={icon}
            isActive={isLinkActive(link)}
            showText={open}
            onClick={() => (link ? goTo(link) : setOpenHelp(true))}
          />
        ))}
      </List>

      {/* margin-top из модуля перебивает сброс отступов у MUI List — прижимаем к низу через sx */}
      <List className={clsx(s.root__list, s.root__list_bottom)} sx={{ marginTop: 'auto' }}>
        <SidebarLink
          text={trackerLink.text}
          icon={trackerLink.icon}
          isActive={isLinkActive(trackerLink.link)}
          showText={open}
          onClick={() => goTo(trackerLink.link)}
        />
        <SidebarLink
          text={v2Group.text}
          icon={v2Group.icon}
          // Свёрнутая группа подсвечивается, если открыт один из её экранов
          isActive={isV2Active && !v2Opened}
          showText={open}
          suffix={v2Opened ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setV2Opened((prev) => !prev)}
        />
        <Collapsed opened={v2Opened} className={s.root__group}>
          {v2Group.links.map(({ text, icon, link }) => (
            <SidebarLink
              key={text}
              text={text}
              icon={icon}
              isActive={isLinkActive(link)}
              showText={open}
              nested
              onClick={() => goTo(link)}
            />
          ))}
        </Collapsed>

        {visibleBottomLinks.map(({ text, icon, link, external }) => (
          <SidebarLink
            key={text}
            text={text}
            icon={icon}
            showText={open}
            onClick={() => {
              if (external) {
                sendEvent(AnalyticsEvent.clickBugReport);
                window.open(link, '_blank', 'noopener,noreferrer');
              } else {
                navigate(link);
              }
            }}
          />
        ))}
      </List>
      <HelpModal open={openHelp} onClose={() => setOpenHelp(false)} />
    </Drawer>
  );
};

export default Sidebar;
